import routes from 'js/core_pre_webpack/routes';
import { keptAlivePost } from 'js/utils/kept_alive_post';
import { HIT_READY_EVENT } from 'js/utils/hitReady';
import { observeTrackingDelivery } from 'js/utils/observeTrackingDelivery';

const IDLE_MS = 60_000;
const INTERACTION_THROTTLE_MS = 1000;
const SCROLL_THROTTLE_MS = 500;
let listening = false;
let tracker: { hitId: number; dispose: () => void } | undefined;

function trackHit(hitId: number): () => void {
  let duration = 0;
  let activeSince: number | null = null;
  let lastInteraction = performance.now();
  let suspended = false;
  let scrollDistance = window.scrollY + window.innerHeight;
  let lastScrollSample = -Infinity;
  let pendingScrollSample: ReturnType<typeof setTimeout> | undefined;
  const storageKey = `${hitId}:duration`;

  try {
    const previous = Number(window.sessionStorage.getItem(storageKey));
    if (Number.isFinite(previous) && previous >= 0) duration = previous;
  } catch {
    // Storage is optional; blocked storage must not prevent delivery.
  }

  const settle = (now: number): void => {
    if (activeSince !== null) {
      duration += Math.max(
        0,
        Math.min(now, lastInteraction + IDLE_MS) - activeSince,
      );
      activeSince = null;
    }
  };

  const resume = (now: number): void => {
    if (
      activeSince !== null &&
      now - lastInteraction < INTERACTION_THROTTLE_MS
    ) {
      lastInteraction = now;
      return;
    }
    settle(now);
    lastInteraction = now;
    if (
      !suspended &&
      document.visibilityState === 'visible' &&
      document.hasFocus()
    ) {
      activeSince = lastInteraction;
    }
  };

  const onInteraction = (): void => resume(performance.now());
  const sampleScrollDistance = (now: number): void => {
    clearTimeout(pendingScrollSample);
    pendingScrollSample = undefined;
    lastScrollSample = now;
    scrollDistance = Math.max(
      scrollDistance,
      window.scrollY + window.innerHeight,
    );
  };

  const send = (sourceEvent: string): void => {
    const now = performance.now();
    settle(now);
    sampleScrollDistance(now);
    const scrollLimit = Math.round(
      document.scrollingElement?.scrollHeight ?? document.body.scrollHeight,
    );
    const data = {
      hit_id: hitId,
      duration_ms: Math.round(duration),
      scroll_distance: Math.min(Math.round(scrollDistance), scrollLimit),
      scroll_distance_limit: scrollLimit,
      source_event: sourceEvent,
    };
    try {
      window.sessionStorage.setItem(storageKey, String(data.duration_ms));
    } catch {
      // Continue without persistence when storage is unavailable.
    }
    void observeTrackingDelivery('page_activity', () =>
      keptAlivePost(
        routes.api_v0_record_page_activity_path({ subdomain_param: 'api' }),
        data,
      ),
    );
  };

  const onVisibility = (): void => {
    if (document.visibilityState === 'hidden') {
      send('visibilitychange');
    } else {
      onInteraction();
    }
  };
  const onPageHide = (): void => {
    suspended = true;
    send('pagehide');
  };
  const onPageShow = (): void => {
    suspended = false;
    onInteraction();
  };
  const onScroll = (): void => {
    const now = performance.now();
    const remaining = SCROLL_THROTTLE_MS - (now - lastScrollSample);
    if (remaining <= 0) {
      sampleScrollDistance(now);
    } else if (pendingScrollSample === undefined) {
      pendingScrollSample = setTimeout(
        () => sampleScrollDistance(performance.now()),
        remaining,
      );
    }
    resume(now);
  };
  const handlers: [string, EventListener][] = [
    ['load', onInteraction],
    ['focus', onInteraction],
    ['blur', () => settle(performance.now())],
    ['pageshow', onPageShow],
    ['pagehide', onPageHide],
    ['scroll', onScroll],
    ['pointerdown', onInteraction],
    ['pointermove', onInteraction],
    ['keydown', onInteraction],
    ['touchstart', onInteraction],
  ];
  handlers.forEach(([name, handler]) => {
    window.addEventListener(name, handler, { passive: true });
  });
  document.addEventListener('visibilitychange', onVisibility);

  send('bundleload');
  onInteraction();

  return () => {
    send('pagehide');
    handlers.forEach(([name, handler]) =>
      window.removeEventListener(name, handler),
    );
    document.removeEventListener('visibilitychange', onVisibility);
  };
}

function startCurrentHit(): void {
  const hitId = window.Aedu?.hit_data?.id;
  if (!hitId || tracker?.hitId === hitId) return;
  tracker?.dispose();
  tracker = { hitId, dispose: trackHit(hitId) };
}

export function startActivityTracking(): void {
  if (typeof window === 'undefined') return;
  if (!listening) {
    window.addEventListener(HIT_READY_EVENT, startCurrentHit);
    listening = true;
  }
  startCurrentHit();
}

export function stopActivityTracking(): void {
  if (typeof window === 'undefined') return;
  window.removeEventListener(HIT_READY_EVENT, startCurrentHit);
  tracker?.dispose();
  tracker = undefined;
  listening = false;
}
